import { BellOutlined } from '@ant-design/icons';
import { Badge, Popover } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { history } from '@umijs/max';
import { withdrawList } from '@/services/letter-backend/withdrawManageController';
import { getAllManualMessage } from '@/services/letter-backend/manualMessageController';

export type SiderTheme = 'light' | 'dark';

/** 浏览器音频上下文，复用避免重复创建 */
let audioCtx: AudioContext | null = null;

const getAudioCtx = () => {
  if (typeof window === 'undefined') return null;
  const AC =
    window.AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) {
    audioCtx = new AC();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

/** 播放一次“叮咚”声（两个音） */
const playChime = (ctx: AudioContext, startAt: number) => {
  [880, 1174.7].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const t = startAt + i * 0.28;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.26);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.3);
  });
};

/** 响铃 3 次，每次间隔约 0.9 秒，整体约 3 秒 */
const playReminder = () => {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const now = ctx.currentTime + 0.05;
  for (let i = 0; i < 3; i += 1) {
    playChime(ctx, now + i * 0.9);
  }
};

/**
 * 消息状态通知：待审核提现 + 待处理人工传话
 * - 有待办时红点 + 红色文字提示
 * - 单一类型待办点击直达对应页面；两类同时存在时弹出汇总供选择
 * - 新增待办时响铃 3 次
 */
export const Question: React.FC = () => {
  const [withdrawCount, setWithdrawCount] = useState<number>(0);
  const [manualCount, setManualCount] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const countsRef = useRef<{ withdraw: number; manual: number } | null>(null);

  const loadPendingCount = async () => {
    let nextWithdraw: number | null = null;
    let nextManual: number | null = null;

    try {
      const res = await withdrawList({ current: 1, pageSize: 1, status: 0 });
      if (res.code === 0) {
        nextWithdraw = res.data?.total ?? 0;
      }
    } catch (e) {
      // 忽略提现通知加载失败
    }

    try {
      const res = await getAllManualMessage({
        current: 1,
        pageSize: 1,
        status: '0',
      });
      if (res.code === 0) {
        nextManual = res.data?.total ?? 0;
      }
    } catch (e) {
      // 忽略人工传话通知加载失败
    }

    const prev = countsRef.current;
    const next = {
      withdraw: nextWithdraw ?? prev?.withdraw ?? 0,
      manual: nextManual ?? prev?.manual ?? 0,
    };

    // 首次加载只记录基线，避免打开页面就响铃
    if (
      prev &&
      (next.withdraw > prev.withdraw || next.manual > prev.manual)
    ) {
      playReminder();
    }

    countsRef.current = next;
    setWithdrawCount(next.withdraw);
    setManualCount(next.manual);
  };

  useEffect(() => {
    // 浏览器要求用户交互后才允许播放声音，首次点击时解锁音频
    const unlock = () => {
      getAudioCtx();
    };
    document.addEventListener('pointerdown', unlock, { once: true });

    // 窄屏（手机）使用紧凑文案
    const mq = window.matchMedia('(max-width: 768px)');
    const updateMq = () => setIsMobile(mq.matches);
    updateMq();
    mq.addEventListener('change', updateMq);

    loadPendingCount();
    const timer = setInterval(loadPendingCount, 30000);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      mq.removeEventListener('change', updateMq);
      clearInterval(timer);
    };
  }, []);

  const total = withdrawCount + manualCount;

  const goWithdraw = () => {
    setOpen(false);
    history.push('/finance/withdraw');
  };

  const goManual = () => {
    setOpen(false);
    history.push('/manual/list');
  };

  // 点击：单一类型直达对应页面；两类并发或暂无待办时展开汇总
  const handleOpenChange = (visible: boolean) => {
    if (!visible) {
      setOpen(false);
      return;
    }
    if (withdrawCount > 0 && manualCount > 0) {
      setOpen(true);
      return;
    }
    if (withdrawCount > 0) {
      history.push('/finance/withdraw');
      return;
    }
    if (manualCount > 0) {
      history.push('/manual/list');
      return;
    }
    setOpen(true);
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '6px 4px',
    fontSize: '13px',
  };

  const content = (
    <div style={{ minWidth: 200 }}>
      {withdrawCount > 0 && (
        <div
          onClick={goWithdraw}
          style={{ ...itemStyle, cursor: 'pointer' }}
        >
          <span>提现申请待审核</span>
          <span style={{ color: '#f5222d', fontWeight: 600 }}>
            {withdrawCount} 条
          </span>
        </div>
      )}
      {manualCount > 0 && (
        <div
          onClick={goManual}
          style={{ ...itemStyle, cursor: 'pointer' }}
        >
          <span>人工传话待处理</span>
          <span style={{ color: '#f5222d', fontWeight: 600 }}>
            {manualCount} 条
          </span>
        </div>
      )}
      {total === 0 && (
        <div style={{ ...itemStyle, color: '#999' }}>暂无待处理事项</div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <a
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px',
          fontSize: '14px',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Badge count={total} size="small" offset={[2, -2]}>
          <BellOutlined
            style={{ fontSize: '18px', color: total > 0 ? '#f5222d' : undefined }}
          />
        </Badge>
        {total === 0 && <span>消息状态通知</span>}
        {withdrawCount > 0 && (
          <span style={{ color: '#f5222d', whiteSpace: 'nowrap' }}>
            {isMobile
              ? `${withdrawCount}条提现`
              : `有 ${withdrawCount} 条提现待审核`}
          </span>
        )}
        {withdrawCount > 0 && manualCount > 0 && (
          <span style={{ color: '#f5222d' }}>{isMobile ? ' ' : '，'}</span>
        )}
        {manualCount > 0 && (
          <span style={{ color: '#f5222d', whiteSpace: 'nowrap' }}>
            {isMobile
              ? `${manualCount}条传话`
              : `有 ${manualCount} 条人工传话待处理`}
          </span>
        )}
      </a>
    </Popover>
  );
};
